import React from 'react'
import { useQuery } from 'react-query'
import { Header, Text, Loader } from '@crossfit/ui'

import type { Image } from 'types'
import InnerContent from 'components/InnerContent'
import PageHeader from 'components/PageHeader'
import * as contentService from 'services/content'
import NotAuthorized from 'components/NotAuthorized'
import usePageTitle from 'hooks/usePageTitle'
import style from './style.module.css'
import { trackEvent } from 'services/segment'
import { useAffiliate } from 'hooks/useAffiliate'

declare global {
  interface Window {
    dataLayer: unknown[]
  }
}

type Kit = {
  headlineText: string
  bodyText: string
  languagesAvailableInText: string
  image: Image
  link: {
    text: string
    url: string
  }
}

type Content = {
  headlineText: string
  subheadlineText: string
  bodyText: string
  kits: Kit[]
}

function useContentKitContent() {
  const { canAccessContentKit } = useAffiliate()
  const query = useQuery(
    'content-kit',
    () => contentService.fetchPage<Content>('/marketing'),
    {
      enabled: canAccessContentKit,
    },
  )

  return {
    hasAccess: canAccessContentKit,
    content: query.data,
    isLoading: query.isLoading,
  }
}

function ContentKitPage() {
  const { content, isLoading, hasAccess } = useContentKitContent()
  const { selectedAffiliate } = useAffiliate()
  usePageTitle(
    `CrossFit Affiliate Content Kits ${
      content ? ` | ${content.headlineText}` : ''
    }`,
  )
  const onClick = (link: string, headlineText: string) => {
    trackEvent({
      event: 'View Marketing Assets Clicked',
      location: 'Affiliate Toolkit',
      linkUrl: link,
      text: 'View Assets',
      tileName: headlineText,
      component: 'Marketing Page',
      affiliateId: selectedAffiliate?.affiliateId,
      userRole:
        selectedAffiliate?.roles?.length > 0
          ? selectedAffiliate?.roles[0]
          : null,
      groupId: selectedAffiliate?.groupId,
    })
    location.href = link
  }

  if (isLoading) {
    return <Loader />
  }

  if (!hasAccess) {
    return <NotAuthorized />
  }

  return (
    <>
      <PageHeader subheadlineText={content.subheadlineText}>
        {content.headlineText}
      </PageHeader>
      <div className={style.main}>
        <InnerContent className={style.innerContent} as='main' variant='xlarge'>
          <ul className={style.kits}>
            {content.kits.map(
              ({
                headlineText,
                image,
                bodyText,
                languagesAvailableInText,
                link,
              }) => (
                <li key={headlineText}>
                  <div className={style.kit}>
                    {image && (
                      <img
                        src={image.sizes.large}
                        alt={image.alt}
                        width={image.sizes.largeWidth}
                        height={image.sizes.largeHeight}
                      />
                    )}
                    <div className={style.content}>
                      <Header
                        as='h4'
                        size='small'
                        className={style.headlineText}
                      >
                        {headlineText}
                      </Header>
                      <Text className={style.bodyText}>{bodyText}</Text>
                      <Text className={style.languages}>Available in:</Text>
                      <Text className={style.category}>
                        {languagesAvailableInText}
                      </Text>
                      <button
                        className={style.link}
                        aria-label={`${link.text} for ${headlineText}`}
                        onClick={() => onClick(link.url, headlineText)}
                      >
                        {link.text}
                      </button>
                    </div>
                  </div>
                </li>
              ),
            )}
          </ul>
        </InnerContent>
      </div>
    </>
  )
}

export default ContentKitPage
